import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { trackMeta } from "../lib/metaPixel";

export default function Contact() {
    const [params] = useSearchParams();
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => {
        const msg = params.get("message");
        if (msg) setForm((f) => ({ ...f, message: decodeURIComponent(msg) }));
    }, [params]);
    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/support-messages", {
                name: form.name,
                email: form.email,
                message: form.message,
            });
            try {
                trackMeta("Contact", { content_name: "support_form" }, { email: form.email });
            } catch { /* silent */ }
            toast.success("Thank you! Our team will reply to your email within 24 hours.");
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <div className="min-h-screen bg-navy-900">
            <section className="bg-navy-950 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                    <div className="label-overline mb-3">Get in touch</div>
                    <h1 className="font-heading text-4xl lg:text-6xl font-bold text-white max-w-3xl">Have a question? <span className="text-gold-500">We&apos;d love to help.</span></h1>
                    <p className="text-white/70 text-base mt-4 max-w-2xl">Drop us a line and our team will get back within 24 hours (Mon–Sat).</p>
                </div>
            </section>
            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
                <form onSubmit={onSubmit} className="rounded-2xl bg-navy-800 border border-white/5 p-8 space-y-4" data-testid="contact-form">
                    <h2 className="font-heading text-2xl font-bold text-white mb-2">Send us a message</h2>
                    <input required value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} placeholder="Full name" className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white" data-testid="contact-name" />
                    <input required type="email" value={form.email} onChange={(e) => setForm({...form, email:e.target.value})} placeholder="Your email address" className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white" data-testid="contact-email" />
                    <textarea required rows={6} value={form.message} onChange={(e) => setForm({...form, message:e.target.value})} placeholder="Tell us your query or the issue you&apos;re facing…" className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white" data-testid="contact-message" />
                    <button disabled={submitting} type="submit" data-testid="contact-submit" className="w-full px-5 py-3 rounded-md bg-gold-500 text-navy-900 font-semibold flex items-center justify-center gap-2 hover:bg-gold-100 disabled:opacity-60">
                        <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send Message"}
                    </button>
                    <div className="text-[11px] text-white/50 text-center pt-1">
                        We&apos;ll reply directly to your email — usually within 24 hours.
                    </div>
                </form>
                <div className="space-y-5">
                    <Item
                        icon={Mail}
                        title="Email Us"
                        lines={["globalpropertymart95@gmail.com", "Fastest way to reach us"]}
                        href="mailto:globalpropertymart95@gmail.com"
                    />
                    <Item
                        icon={Clock}
                        title="Response Time"
                        lines={["Under 24 hours · Mon–Sat", "9 AM – 8 PM IST"]}
                    />
                    <Item
                        icon={MapPin}
                        title="Head Office"
                        lines={["Global Property Mart", "Wardha Road, Nagpur, Maharashtra, India"]}
                    />
                </div>
            </section>
        </div>
    );
}

function Item({ icon: Icon, title, lines, href }) {
    const content = (
        <div className="rounded-2xl bg-navy-800 border border-white/5 p-6 flex gap-4 transition-colors hover:border-gold-500/40">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-gold-500/10 border border-gold-500/30">
                <Icon className="w-5 h-5 text-gold-500" />
            </div>
            <div>
                <h4 className="font-heading text-lg font-bold text-white">{title}</h4>
                {lines.map((l) => <div key={l} className="text-sm text-white/65">{l}</div>)}
            </div>
        </div>
    );
    if (href) {
        return <a href={href} className="block">{content}</a>;
    }
    return content;
}
